import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import * as navigation from 'app-level/navigation';
import { useTourContext } from 'tours/TourProvider';
import { Dropdown, PillFilters } from 'ui-kit';
import { createQueryFilters, DataContext, DataContextActionType } from 'context/DataContext';
import { filtersToQueryFilters } from 'features/common-elements';
import {
  keysToAbbr,
  resolveSelectedStrats,
} from 'features/drilldown/Stratifications/parseAbbrString';
import { StratKey } from 'features/drilldown/Stratifications/startifications.config';
import { StratCard } from 'features/drilldown/Stratifications/StratCard';
import { StratsSelection } from 'features/drilldown/Stratifications/StratsSelection';
import { PortfolioFilterConfig } from 'features/pages/portfolio/Portfolio/portfolioFilters.config';
import { useSearchParams } from 'react-router-dom';

import { usePoolOptions } from './hooks/usePoolOptions';
import { usePoolSelection } from './hooks/usePoolSelection';
import { usePoolPerformance } from './gql/getPoolPerformance_gql';
import { useSyncSelectedPoolsData } from './hooks/useSyncSelectedPoolsData';

import { useQuery } from '@apollo/client';

import { AssetClass, Filter, FilterableField, FilterOperator } from '__generated__/globalTypes';
import { GetDrilldown, GetDrilldownVariables } from 'query/__generated__/drilldown';
import { GET_DRILLDOWN_QUERY } from 'query/drilldown';

import { PortfolioLayout } from './Portfolio/PortfolioLayout';
import { usePortfolioFilters } from './Portfolio/usePortfolioFilters';

import { Pool } from 'query/getPools';
const getDrilldownVariables = (
  assetClass: AssetClass | null,
  filters: Filter[],
): GetDrilldownVariables => {
  const queryFilters = createQueryFilters(assetClass, filters);

  return {
    sort: {},
    pagination: {},
    filters: queryFilters,
  };
};

const Stratifications = () => {
  const assetClass = navigation.usePortfolioAssetClass();
  const gqlAssetClass = assetClass;
  const filterProps = usePortfolioFilters();
  const { setPool } = usePortfolioFilters();
  const { dispatch } = useContext(DataContext);
  const { startTour, tourDataLoading } = useTourContext();
  const hasStartedTours = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTourRef = useRef(startTour);
  const retryAttemptsRef = useRef(0);
  const [selectedPool, setSelectedPool] = useState<{
    label: string;
    value: string;
    poolData: Pool;
  } | null>(null);

  // Pool options for dropdown
  const { poolOptions } = usePoolOptions({
    assetClass: assetClass,
  });

  const queryFilters = filterProps.filtersConfig
    ? filtersToQueryFilters<PortfolioFilterConfig>(
        filterProps.currentFilters,
        filterProps.filtersConfig,
      )
    : [];
  const [stratsSearch, setStratsSearch] = useSearchParams();
  const abbrStr = stratsSearch.get('strats');
  const selectedStratsFromUrl = useMemo(() => resolveSelectedStrats(abbrStr), [abbrStr]);

  // Use pool performance query when pool is selected, otherwise use company query
  const { data: poolData, loading: poolLoading } = usePoolPerformance(
    selectedPool?.value || null,
    queryFilters,
  );

  const { data: companyData, loading: companyLoading } = useQuery<
    GetDrilldown,
    GetDrilldownVariables
  >(GET_DRILLDOWN_QUERY, {
    variables: gqlAssetClass
      ? getDrilldownVariables(gqlAssetClass, queryFilters)
      : {
          sort: {},
          pagination: {},
          filters: createQueryFilters(gqlAssetClass, queryFilters),
        },
    skip: !gqlAssetClass || !!selectedPool, // Skip if pool is selected
    fetchPolicy: 'cache-and-network',
  });

  // Use pool data if pool is selected, otherwise use company data
  const data = selectedPool ? poolData : companyData;
  const loading = selectedPool ? poolLoading : companyLoading;

  // Reuse pool performance DSCR to avoid a duplicate Lambda invoke on this page.
  useSyncSelectedPoolsData(selectedPool ? [selectedPool.poolData] : [], {
    skipLazyFetch: true,
    dscrMetrics: poolData?.user?.company?.pool?.performance_summary ?? null,
  });

  useEffect(() => {
    dispatch({
      type: DataContextActionType.SET_LOADING,
      payload: loading,
    });
  }, [loading]);

  useEffect(() => {
    startTourRef.current = startTour;
  }, [startTour]);

  // Auto-start tour on page load (wait for data to be ready)
  useEffect(() => {
    if (hasStartedTours.current) {
      return;
    }

    if (tourDataLoading || !gqlAssetClass || !filterProps.filtersConfig || loading) {
      return;
    }

    const checkTourElementsReady = (): boolean => {
      const firstElement = document.querySelector(
        '[data-tour="stratifications-portfolio-summary"]',
      );
      return firstElement !== null;
    };

    const attemptStartTour = () => {
      if (hasStartedTours.current || tourDataLoading || loading) {
        return;
      }

      if (checkTourElementsReady()) {
        retryAttemptsRef.current = 0;
        timeoutRef.current = setTimeout(() => {
          if (hasStartedTours.current || tourDataLoading || loading) {
            return;
          }
          hasStartedTours.current = true;
          startTourRef.current('portfolio.stratifications');
          timeoutRef.current = null;
        }, 300);
      } else if (retryAttemptsRef.current < 20) {
        retryAttemptsRef.current += 1;
        timeoutRef.current = setTimeout(attemptStartTour, 150);
      } else {
        console.warn('[Tour] Stratifications tour elements not found after max retries');
      }
    };

    attemptStartTour();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [tourDataLoading, gqlAssetClass, filterProps.filtersConfig, loading]);

  // Handle pool initialization and reset logic
  usePoolSelection({
    setSelectedPool,
  });

  const handlePoolChange = (option: { label: string; value: string; poolData?: Pool } | null) => {
    if (!option || !option.poolData) {
      setSelectedPool(null);
      setPool(null);
      return;
    }

    setSelectedPool(
      option as {
        label: string;
        value: string;
        poolData: Pool;
      },
    );
    setPool(option.value);
  };

  function updateSelectedStrats(strats: StratKey[]) {
    const str = keysToAbbr(strats);
    const searchParams = new URLSearchParams(stratsSearch);
    searchParams.set('strats', str);
    setStratsSearch(searchParams);
  }

  if (!gqlAssetClass || !filterProps.filtersConfig) {
    return <PortfolioLayout />;
  }

  const dataToShow = data;

  // Extract performance data based on whether we're showing pool or company data
  const performanceData = selectedPool
    ? (dataToShow as any)?.user?.company?.pool?.performance_summary
    : (dataToShow as any)?.user?.company?.performanceData;

  return (
    <PortfolioLayout
      filterRow={
        <div className="flex w-full flex-col gap-2">
          <StratsSelection
            selectedStrats={selectedStratsFromUrl}
            onSelectedStratsChange={updateSelectedStrats}
          />
          <div className="flex w-full items-center justify-between gap-4">
            <div className="flex-1" data-tour="stratifications-pill-filters">
              <PillFilters
                {...filterProps}
                filtersConfig={filterProps.filtersConfig}
                isLoading={loading}
              />
            </div>
            <div className="flex items-center gap-2" data-tour="stratifications-pool-selector">
              <span className="text-sm text-gray-600">Pool:</span>
              <Dropdown
                options={poolOptions || []}
                value={selectedPool?.label || ''}
                className="w-48 border-none"
                placeholder="Select Pool"
                onValueChange={handlePoolChange}
                onClear={() => handlePoolChange(null)}
                showClear={true}
              />
            </div>
          </div>
        </div>
      }
    >
      {performanceData ? (
        <div
          className="mt-2 flex flex-wrap gap-2
        [&>*]:max-h-[700px]
        [&>*]:min-w-full
        [&>*]:flex-1
        [&>*]:lg:min-w-[calc(50%-0.5rem)]
        [&>*]:2xl:min-w-[calc(33.333%-0.5rem)]"
        >
          {selectedStratsFromUrl.map((strat, index) => (
            <StratCard
              key={strat}
              strat={strat}
              assetClass={gqlAssetClass}
              summary={performanceData}
              avgSummary={performanceData}
              data-tour={index === 0 ? 'stratifications-first-strat-card' : undefined}
            />
          ))}
        </div>
      ) : (
        <div className="flex h-64 items-center justify-center text-gray-500">No data available</div>
      )}
    </PortfolioLayout>
  );
};

export default Stratifications;
